import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { lazy, Suspense } from "react";
import denverLifeLogo from "@/assets/denver-life-logo-v2.webp";
import urlacherInterview from "@/assets/urlacher-interview.webp";

const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/media-outlet")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/media-outlet/" }],
    meta: [
      { title: "Media Coverage | RESTORE® Hair" },
      {
        name: "description",
        content:
          "Magazines, TV stations, newspapers and online outlets nationwide have covered the RESTORE® No-Shave FUE™ procedure and our celebrity clients.",
      },
      { property: "og:title", content: "Media Coverage — RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "See where RESTORE® Hair and its celebrity clients — including Brian Urlacher, Deion Sanders and Ryne Sandberg — have been featured in the press.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: MediaOutletPage,
});

const brandLogos = [
  { src: "/img/r/wp-2022-04-new-logo1.webp", alt: "Chicago Tribune Logo" },
  { src: "/img/r/wp-2022-04-new-logo2.webp", alt: "ESPN Logo" },
  { src: "/img/r/wp-2022-04-new-logo3.webp", alt: "Los Angeles Times Logo" },
  { src: "/img/r/wp-2022-04-new-logo4.webp", alt: "GQ Logo" },
  { src: "/img/r/wp-2022-04-new-logo5.webp", alt: "INC Logo" },
  { src: "/img/r/wp-2022-04-new-logo6.webp", alt: "NBC Sports Logo" },
];

type Article = {
  image: string;
  logo?: string;
  logoClassName?: string;
  date: string;
  title?: string;
  url: string;
};

const articles: Article[] = [
  {
    image: "/img/r/wp-2025-11-deion.webp",
    logo: "/press-logos/psychology-today.svg",
    logoClassName: "h-8 w-auto object-contain",
    date: "November 13, 2025",
    title: "How Embracing Vulnerability Redefines Strength",
    url: "https://www.psychologytoday.com/us/blog/in-the-trenches/202510/how-embracing-vulnerability-redefines-strength",
  },
  {
    image: "/img/r/wp-2024-10-deion-and-dr-harris-pic.webp",
    logo: denverLifeLogo,
    logoClassName: "h-10 w-auto object-contain",
    date: "October 14, 2024",
    title: "Deion Sanders Reclaims Confidence Thanks To Restore",
    url: "https://www.denverlifemagazine.com/deion-sanders-reclaims-confidence-thanks-to-restore/",
  },
  {
    image: "/img/r/wp-2023-06-dr-harris-scaled.webp",
    logo: "/press-logos/medium.svg",
    logoClassName: "h-9 w-auto object-contain",
    date: "June 12, 2023",
    title:
      "Dr James Harris Of RESTORE: 5 Things I Wish Someone Told Me When I First Became A Doctor",
    url: "https://medium.com/authority-magazine/dr-james-harris-of-restore-5-things-i-wish-someone-told-me-when-i-first-became-a-doctor-19811caecc60",
  },
  {
    image: "/img/r/wp-2023-06-ozempic.webp",
    date: "June 1, 2023",
    title:
      "Does Ozempic cause hair loss? Doctors reveal the truth about the drug's side effects",
    url: "https://www.womanandhome.com/health-wellbeing/does-ozempic-cause-hair-loss/",
  },
  {
    image: "/img/r/wp-2021-12-nbc-post.webp",
    logo: "/img/r/wp-2021-12-five-logo.webp",
    date: "December 30, 2021",
    title: "Brian Urlacher Says Goodbye to Being Bald",
    url: "https://www.nbcchicago.com/news/local/brian-urlacher-says-goodbye-to-being-bald/2014072/",
  },
  {
    image: urlacherInterview,
    logo: "/img/r/wp-2021-11-gq-logo-300x159.webp",
    date: "November 17, 2021",
    title: "Brian Urlacher Had Surgery for 8 Hours to Get His Hair Back",
    url: "https://www.gq.com/story/brian-urlacher-hair-surgery-bald-cure",
  },
  {
    image: "/img/r/wp-2021-11-goodmorning-1.webp",
    logo: "/img/r/wp-2021-11-wls-1-300x150.webp",
    date: "November 17, 2021",
    title: "Deion Sanders and Brian Urlacher get personal about hair restoration",
    url: "https://www.wfaa.com/article/entertainment/television/programs/good-morning-texas/deion-sanders-and-brian-urlacher-get-personal-about-hair-restoration/287-561885528",
  },
  {
    image: "/img/r/wp-2021-11-windycity-post-1.webp",
    logo: "/img/r/wp-2021-11-wls-1-300x150.webp",
    date: "November 17, 2021",
    title: "OMG! Brian Urlacher Has Hair",
    url: "https://abc7chicago.com/sports/omg-brian-urlacher-has-hair/1146961/",
  },
  {
    image: "/img/r/wp-2021-11-dallastv-post-1.webp",
    logo: "/img/r/wp-2021-11-nbcdfw-e1592586695208-300x141.webp",
    date: "November 17, 2021",
    title: "NFL Hall of Famers Enjoy 'Hair-Raising' Experience",
    url: "https://www.nbcdfw.com/news/sports/Hair_Raising_Experience_Dallas-Fort-Worth-485081991.html",
  },
  {
    image: "/img/r/wp-2021-11-wgn-post-1.webp",
    logo: "/img/r/wp-2021-11-wgn-9-news-logo-300x171.webp",
    date: "November 17, 2021",
    title: "Former Bears linebacker Brian Urlacher debuts his new hair.",
    url: "https://wgntv.com/2016/01/05/former-bears-linebacker-brian-urlacher-reveals-his-new-head-full-of-hair-on-morning-news/",
  },
  {
    image: "/img/r/wp-2021-11-ryne-wgn-post-1.webp",
    logo: "/img/r/wp-2021-11-wgn-9-news-logo-300x171.webp",
    date: "November 17, 2021",
    title: "Cubs great Ryne Sandberg reveals new head of hair on WGN",
    url: "https://wgntv.com/2017/09/14/cubs-great-ryne-sandberg-reveals-new-head-of-hair-on-wgn/",
  },
  {
    image: "/img/r/wp-2021-11-screen-shot-2020-10-01-at-10-52-51-am-1.webp",
    logo: "/img/r/wp-2021-11-si-900x598-1-300x199.webp",
    date: "November 17, 2021",
    title: "Brian Urlacher Has Hair Now",
    url: "https://www.si.com/extra-mustard/2016/01/05/brian-urlacher-bears-hair-bald-photo",
  },
  {
    image: "/img/r/wp-2021-11-the-ticket-post-1.webp",
    logo: "/img/r/wp-2021-11-image-89.webp",
    date: "November 17, 2021",
    title: "Brian Urlacher and Deion Sanders talk about Hair and office in Dallas",
    url: "https://omny.fm/shows/the-ticket-top-10/musers-deion-sanders-and-brian-urlacher",
  },
  {
    image: "/img/r/wp-2021-11-rectangle-2-12-1.webp",
    logo: "/img/r/wp-2021-11-espn-logo-300x112.webp",
    date: "November 17, 2021",
    title: "Brian Urlacher's new hairdo; every playoff team's kryptonite",
    url: "https://espn.go.com/nfl/story/_/page/whatstrending160105/trending-nfl-brian-urlacher-new-hairdo-every-playoff-team-kryptonite",
  },
  {
    image: "/img/r/wp-2021-11-equire-post-1.webp",
    logo: "/img/r/wp-2021-11-esquire-logo-300x169.webp",
    date: "November 17, 2021",
    title: "Where Did Brian Urlacher Get Hair?",
    url: "https://www.esquire.com/sports/news/a40998/brian-urlacher-hair/",
  },
  {
    image: "/img/r/wp-2021-11-washington-post-1-1.webp",
    logo: "/img/r/wp-2021-11-1200px-chicago-tribune-logo-300x46.webp",
    logoClassName: "h-14 md:h-16 w-auto object-contain",
    date: "November 17, 2021",
    title: "Former NFL Player Brian Urlacher Shows Off Hair Transplant",
    url: "https://www.washingtonpost.com/news/early-lead/wp/2016/01/05/former-nfl-player-brian-urlacher-shows-off-hair-transplant/",
  },
  {
    image: "/img/r/wp-2021-11-rectangle-2-10-1-1.webp",
    logo: "/img/r/wp-2021-11-chicago-tribune-logo-blue-background-300x68.webp",
    logoClassName: "h-14 md:h-16 w-auto object-contain",
    date: "November 17, 2021",
    title: "Ryne Sandberg follows in Urlacher's footsteps as hair-transplant endorser",
    url: "https://www.chicagotribune.com/2017/09/14/ryne-sandberg-follows-in-brian-urlachers-footsteps-as-hair-transplant-endorser/",
  },
  {
    image: "/img/r/wp-2021-11-rectangle-2-13-1.webp",
    logo: "/img/r/wp-2021-11-inc-logo-300x168.webp",
    date: "November 17, 2021",
    title: "Why These Identical Twins Dream of a Chicago Without Baldness",
    url: "https://www.inc.com/leigh-buchanan/restore-is-making-chicago-the-hairy-city.html",
  },
  {
    image: "/img/r/wp-2021-11-rectangle-2-11.webp",
    logo: "/img/r/wp-2021-11-new-york-post-logo-red-300x70.webp",
    date: "November 17, 2021",
    title: "The world wasn't ready for Brian Urlacher's full head of hair",
    url: "https://nypost.com/2016/01/05/brian-urlacher-shows-off-shocking-full-head-of-hair/",
  },
  {
    image: "/img/r/wp-2021-11-rectangle-2-9.webp",
    logo: "/img/r/wp-2021-11-chicago-tribune-logo-vector-300x200.webp",
    date: "November 17, 2021",
    title: "The hair man who bagged Urlacher, Chicago's most famous bald Bear",
    url: "https://www.chicagotribune.com/business/ct-urlacher-hair-transplant-company-exec-qa-0403-biz-20160401-story.html",
  },
  {
    image: "/img/r/wp-2021-11-rectangle-2-8.webp",
    logo: "/img/r/wp-2021-11-latimes-logo-300x60.webp",
    date: "November 17, 2021",
    title: "Bears legend Brian Urlacher has hair now, and he looks ... different",
    url: "https://www.latimes.com/sports/sportsnow/la-sp-sn-brian-urlacher-hair-20160105-story.html",
  },
];

function ArticleCard({ article }: { article: Article }) {
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group mb-6 flex break-inside-avoid flex-col overflow-hidden rounded-none border border-border bg-card shadow-sm transition hover:shadow-md md:mb-8"
    >
      <div className="w-full overflow-hidden bg-muted">
        <img
          src={article.image}
          alt={article.title ?? "RESTORE Hair in the media"}
          loading="lazy"
          className="h-auto w-full rounded-none object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        {article.logo && (
          <img
            src={article.logo}
            alt=""
            loading="lazy"
            className={`rounded-none ${article.logoClassName ?? "h-10 w-auto object-contain"}`}
          />
        )}
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {article.date}
        </p>
        {article.title && (
          <h3 className="text-base font-semibold leading-snug text-foreground">
            {article.title}
          </h3>
        )}
        <span className="mt-auto inline-flex items-center text-sm font-semibold text-primary group-hover:underline">
          View Original Article →
        </span>
      </div>
    </a>
  );
}

function MediaOutletPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="border-b border-border bg-background">
          <div className="mx-auto max-w-6xl px-4 py-14 md:py-20">
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground text-center">
              Everybody's talking about RESTORE®!
            </h1>
            <p className="mx-auto mt-6 max-w-4xl text-center text-base md:text-lg leading-relaxed text-muted-foreground">
              After Brian Urlacher stunned the world with his new head of hair,
              more athletes followed his lead. Magazines, TV stations, online
              outlets and newspapers from coast to coast have showcased the
              breakthrough RESTORE® No-Shave FUE™ procedure and our growing
              list of happy celebrity clients.
            </p>
          </div>
        </section>

        <section className="border-y border-border bg-neutral-700">
          <div className="mx-auto max-w-6xl px-4 py-8 md:py-10">
            <div className="grid grid-cols-3 md:grid-cols-6 items-center gap-6 md:gap-10">
              {brandLogos.map((logo) => (
                <img
                  key={logo.src}
                  src={logo.src}
                  alt={logo.alt}
                  loading="lazy"
                  className="mx-auto h-9 md:h-12 w-auto rounded-none object-contain"
                />
              ))}
            </div>
          </div>
        </section>

        <section className="bg-muted/30">
          <div className="mx-auto max-w-6xl px-4 py-14 md:py-20">
            <h2 className="sr-only">Media Coverage</h2>
            <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 md:gap-8">
              {articles.map((a) => (
                <ArticleCard key={a.url} article={a} />
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background">
          <div className="mx-auto max-w-6xl px-4 py-14 md:py-20">
            <Suspense fallback={null}>
              <QuoteForm />
            </Suspense>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
